import { useQuery } from '@tanstack/react-query'

import { DEFAULT_RECEIPT_TEMPLATE, type ReceiptTemplate } from '@aeris/printing'

import { fetchCompany } from './api/organization.ts'
import { fetchReceiptTemplate } from './api/receipt-template.ts'
import { useAuth } from './auth-context.ts'
import { companyHeaderFrom, type PrintedCompany } from './company-header.ts'

/**
 * O modelo da nota e o cabeçalho da loja, para quem imprime (F149).
 *
 * Uma chave de cache para cada um, e `staleTime` de cinco minutos: **nenhuma
 * requisição a mais por impressão**. Enquanto a resposta não chega, vale o
 * padrão — que é o papel de hoje —, e não uma tela em branco.
 *
 * O cabeçalho usa a MESMA chave `['company', id]` que o PDV, o menu e o Estoque
 * já usam: quando o diálogo do recibo abre, a resposta costuma estar em cache.
 */

export const useReceiptTemplate = (): ReceiptTemplate => {
  const { allows } = useAuth()
  const query = useQuery({
    queryKey: ['receipt-template'],
    queryFn: async ({ signal }) => await fetchReceiptTemplate(signal),
    enabled: allows('organization.read'),
    staleTime: 5 * 60_000,
  })

  return query.data?.template ?? DEFAULT_RECEIPT_TEMPLATE
}

export const useCompanyHeader = (): PrintedCompany => {
  const { session, allows } = useAuth()
  const companyId = session?.context?.companyId ?? null

  const query = useQuery({
    queryKey: ['company', companyId],
    queryFn: async ({ signal }) => await fetchCompany(companyId ?? '', signal),
    enabled: companyId !== null && allows('organization.read'),
    staleTime: 5 * 60_000,
  })

  return companyHeaderFrom(
    query.data?.company,
    session?.context?.companyName ?? 'Aeris One'
  )
}
